import { defineStore } from 'pinia'
import axios from '../utils/axios-interceptors.js'
import { 
  setAccessToken, 
  clearAccessToken, 
  getAccessToken,
  startTokenRefreshTimer, 
  stopTokenRefreshTimer 
} from '../utils/axios-interceptors.js'
import swManager from '../utils/sw-manager.js'
import { buildGoogleLoginHref } from '../utils/line-browser.js'

export const useUserStore = defineStore('user', {
  state: () => {
    // 安全地從 localStorage 讀取資料
    let currentUser = null
    let isLoggedIn = false
    let unreadNotificationsCount = 0
    
    try {
      const storedUser = localStorage.getItem('currentUser')
      if (storedUser) {
        currentUser = JSON.parse(storedUser)
      }
      isLoggedIn = localStorage.getItem('isLoggedIn') === 'true'
      unreadNotificationsCount = parseInt(localStorage.getItem('unreadNotificationsCount')) || 0
    } catch (error) {
      console.warn('讀取 localStorage 失敗，使用預設值:', error)
    }
    
    return {
      currentUser,
      isLoggedIn,
      isLoading: false,
      showUserMenu: false,
      token: '',
      unreadNotificationsCount,
      isJwtAuth: false, // 是否使用 JWT 認證
      tokenExpiresAt: null // Token 過期時間
    }
  },
  
  getters: {
    userAvatar: (state) => {
      if (state.currentUser?.avatar) {
        return state.currentUser.avatar
      }
      return '/user.png'
    },
    userName: (state) => state.currentUser?.name || '訅客',
    userNickname: (state) => state.currentUser?.nickname || '訪客',
    userId: (state) => state.currentUser?.id || null,
    isAdmin: (state) => state.currentUser?.admin === true
  },
  
  actions: {
    // 初始化認證狀態
    async initAuth() {
      this.token = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
      
      // 從 localStorage 恢復 JWT 狀態
      const storedJwtAuth = localStorage.getItem('isJwtAuth')
      if (storedJwtAuth === 'true') {
        this.isJwtAuth = true
        startTokenRefreshTimer()
      }
      
      await this.checkAuthStatus()
    },
    
    // 檢查認證狀態
    async checkAuthStatus() {
      this.isLoading = true
      try {
        const response = await axios.get('/api/v1/auth/status', {
          headers: {
            'Accept': 'application/json'
          },
          withCredentials: true
        })
        
        if (response.data.logged_in) {
          this.setUserData(response.data.user, response.data.jwt_authenticated)
          
          // 快取認證狀態到 Service Worker
          await swManager.cacheAuthData(response.data)
          
          console.log('用戶認證成功，user data:', {
            user: response.data.user,
            uid: response.data.user?.uid,
            jwt_authenticated: response.data.jwt_authenticated
          })
          // 登入成功後載入未讀通知數量
          await this.loadUnreadNotificationsCount()
        } else {
          this.clearUserData()
          console.log('用戶未登入')
        }
      } catch (error) {
        console.warn('檢查認證狀態失敗:', error.message)
        this.clearUserData()
      } finally {
        this.isLoading = false
      }
    },
    
    // Google OAuth2 登入
    googleLogin() {
      // LINE 內建瀏覽器會透過官方參數交由系統預設瀏覽器開啟。
      window.location.href = buildGoogleLoginHref()
    },

    // 處理 OAuth 登入成功回調（包含 JWT）
    async handleOAuthSuccess(userData, tokens) {
      if (tokens && tokens.access_token) {
        // 設定 JWT token
        setAccessToken(tokens.access_token)
        this.isJwtAuth = true
        this.tokenExpiresAt = Date.now() + (tokens.expires_in * 1000)
        
        // 啟動定時刷新
        startTokenRefreshTimer()
        
        // 儲存 JWT 狀態
        localStorage.setItem('isJwtAuth', 'true')
      }
      
      // 設定用戶資料
      this.setUserData(userData, !!tokens)
      
      console.log('OAuth 登入成功，JWT 認證已啟用')
    },
    
    // 登出
    async logout() {
      this.isLoading = true
      try {
        await axios.delete('/api/v1/auth/logout', {
          headers: {
            'X-CSRF-Token': this.token,
            'Accept': 'application/json'
          }
        })
        
        // 清除 JWT 相關狀態
        if (this.isJwtAuth) {
          clearAccessToken()
          stopTokenRefreshTimer()
        }
        
        // 清除 Service Worker 快取
        await swManager.clearAuthCache()
        
        this.clearUserData()
        this.showUserMenu = false
        
        // 重新載入頁面以確保所有狀態重置
        window.location.reload()
      } catch (error) {
        console.error('登出失敗:', error.message)
      } finally {
        this.isLoading = false
      }
    },
    
    // 切換用戶選單顯示
    toggleUserMenu() {
      this.showUserMenu = !this.showUserMenu
    },
    
    // 關閉用戶選單
    closeUserMenu() {
      this.showUserMenu = false
    },
    
    // 取得使用者加入的武場列表
    async getUserClubs() {
      try {
        const response = await axios.get('/api/v1/users/clubs', {
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': this.token
          },
          withCredentials: true
        })
        
        return response.data
      } catch (error) {
        console.error('取得使用者武場列表失敗:', error)
        return []
      }
    },

    // 載入未讀通知數量
    async loadUnreadNotificationsCount() {
      if (!this.isLoggedIn) {
        this.unreadNotificationsCount = 0
        return
      }
      
      try {
        const response = await axios.get('/home/unread_notifications_count', {
          headers: {
            'Accept': 'application/json'
          },
          withCredentials: true
        })
        
        this.unreadNotificationsCount = response.data.count || 0
        // 將未讀通知數量儲存到 localStorage
        try {
          localStorage.setItem('unreadNotificationsCount', this.unreadNotificationsCount.toString())
        } catch (error) {
          console.warn('儲存未讀通知數量失敗:', error)
        }
        console.log('未讀通知數量:', this.unreadNotificationsCount)
      } catch (error) {
        console.error('載入未讀通知數量失敗:', error)
        this.unreadNotificationsCount = 0
      }
    },

    // 重置未讀通知數量（當用戶查看通知或標記為已讀時）
    clearUnreadNotifications() {
      this.unreadNotificationsCount = 0
      try {
        localStorage.setItem('unreadNotificationsCount', '0')
      } catch (error) {
        console.warn('清除未讀通知數量失敗:', error)
      }
    },

    // 設定用戶資料並儲存到 localStorage
    setUserData(userData, isJwtAuth = false) {
      this.isLoggedIn = true
      this.currentUser = userData
      this.isJwtAuth = isJwtAuth
      
      // 安全地儲存到 localStorage
      try {
        localStorage.setItem('isLoggedIn', 'true')
        localStorage.setItem('currentUser', JSON.stringify(userData))
        localStorage.setItem('isJwtAuth', isJwtAuth.toString())
      } catch (error) {
        console.warn('儲存使用者資料到 localStorage 失敗:', error)
      }
    },

    // 清除用戶資料並從 localStorage 移除
    clearUserData() {
      this.isLoggedIn = false
      this.currentUser = null
      this.unreadNotificationsCount = 0
      this.isJwtAuth = false
      this.tokenExpiresAt = null
      
      // 清除 JWT 相關狀態
      clearAccessToken()
      stopTokenRefreshTimer()
      
      // 安全地從 localStorage 移除
      try {
        localStorage.removeItem('isLoggedIn')
        localStorage.removeItem('currentUser')
        localStorage.removeItem('unreadNotificationsCount')
        localStorage.removeItem('isJwtAuth')
      } catch (error) {
        console.warn('清除 localStorage 資料失敗:', error)
      }
    }
  }
})
